import * as React from "react";

import { Separator } from "@replo/design-system/components/separator/Separator";
import { Spinner } from "@replo/design-system/components/spinner/Spinner";
import DeprecatedButton from "@v1/components/designSystem/button/Button";
import ArticleElementEditor from "@v1/components/editor/elementEditors/ArticleElementEditor";
import PageElementEditor from "@v1/components/editor/elementEditors/PageElementEditor";
import ProductTemplateElementEditor from "@v1/components/editor/elementEditors/ProductTemplateElementEditor";
import SectionElementEditor from "@v1/components/editor/elementEditors/SectionElementEditor";
import { ElementEditorDataContext } from "@v1/contexts/ElementEditorDataContext";
import {
  useElementEditorErrorContext,
  withElementEditorErrorContext,
} from "@v1/contexts/ElementEditorErrorContext";
import { useEditableElement } from "@v1/hooks/useEditableElement";
import { elementFormHasErrors } from "@v1/utils/element";
import { useParams } from "react-router-dom";
import { exhaustiveSwitch } from "replo-utils/lib/misc";
import { reploElementMetadataSchema } from "schemas/element";

const UpdateElementPane = withElementEditorErrorContext(() => {
  const { elementId } = useParams();
  const {
    element,
    onChangeElement,
    validateElement,
    isUpdatingElement,
    updateElementMetadata,
  } = useEditableElement({
    elementId,
  });

  const { errorMapping, setErrors, clearErrors } =
    useElementEditorErrorContext();

  if (!element) {
    return (
      <div className="flex h-full flex-col items-center justify-center p-4">
        <Spinner variant="primary" className="size-6" />
      </div>
    );
  }

  function onSubmit() {
    clearErrors("path", "allErrorsFromAllFormKeys");

    const { isValid, errors: validationErrors } = validateElement(element!);
    if (!isValid) {
      if (validationErrors.title.length > 0) {
        setErrors("title", validationErrors.title);
      }
      if (validationErrors.path.length > 0) {
        setErrors("path", validationErrors.path);
      }
      return;
    }

    const elementMetadata = reploElementMetadataSchema.parse(element);

    void updateElementMetadata(elementMetadata);
  }

  return (
    <ElementEditorDataContext.Provider value={{ element, onChangeElement }}>
      <div className="flex min-h-0 flex-1 flex-col">
        {/* NOTE (Cole, 2025-02-06): The scrollbar stuff is here to hide the scrollbar across major browsers without needing a tailwind plugin */}
        <div className="min-h-0 flex-1 overflow-x-hidden p-3 pt-0 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {exhaustiveSwitch(element)({
            page: () => <PageElementEditor />,
            shopifyArticle: () => <ArticleElementEditor />,
            shopifyProductTemplate: () => <ProductTemplateElementEditor />,
            shopifySection: () => <SectionElementEditor requestType="update" />,
          })}
        </div>

        <div className="sticky bottom-0 bg-white p-3">
          <div className="w-full pb-1.5">
            <Separator />
          </div>

          <DeprecatedButton
            size="base"
            variant="primary"
            type="submit"
            isLoading={isUpdatingElement}
            onClick={onSubmit}
            disabled={elementFormHasErrors(errorMapping)}
          >
            Save Settings
          </DeprecatedButton>
        </div>
      </div>
    </ElementEditorDataContext.Provider>
  );
});

export default UpdateElementPane;
